import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AppAccessActionResponse,
  LearnerAppConfigResponse,
  LearnerAppConfigUpdateRequest,
} from "@eduviaos/shared";
import { api } from "@/lib/api";
import { studentKeys } from "@/hooks/useStudents";

export const learnerAppConfigKeys = {
  all: ["learner-app-config"] as const,
  me: () => [...learnerAppConfigKeys.all, "me"] as const,
};

export function useLearnerAppConfig(enabled = true) {
  return useQuery<LearnerAppConfigResponse>({
    queryKey: learnerAppConfigKeys.me(),
    queryFn: () => api.get<LearnerAppConfigResponse>("/me/learner-app-config"),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function useUpdateLearnerAppConfig() {
  const qc = useQueryClient();
  return useMutation<LearnerAppConfigResponse, Error, LearnerAppConfigUpdateRequest>({
    mutationFn: (body) => api.put<LearnerAppConfigResponse>("/me/learner-app-config", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: learnerAppConfigKeys.all });
    },
  });
}

export function useGrantAppAccess() {
  const qc = useQueryClient();
  return useMutation<AppAccessActionResponse, Error, string>({
    mutationKey: ["learner-app-access", "grant"],
    mutationFn: (studentId) =>
      api.post<AppAccessActionResponse>(`/me/students/${studentId}/grant-app-access`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: studentKeys.all });
    },
  });
}

export function useRevokeAppAccess() {
  const qc = useQueryClient();
  return useMutation<AppAccessActionResponse, Error, string>({
    mutationKey: ["learner-app-access", "revoke"],
    mutationFn: (studentId) =>
      api.post<AppAccessActionResponse>(`/me/students/${studentId}/revoke-app-access`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: studentKeys.all });
    },
  });
}

export function requiresAppAccessGrant(pricingMode: string | undefined): boolean {
  return pricingMode === "separate" || pricingMode === "sa_push";
}

export function formatLearnerAppPrice(paise: number): string {
  const rupees = paise / 100;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: rupees % 1 === 0 ? 0 : 2,
  }).format(rupees);
}
